import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/shared/ui';
import { CustomTypography } from '@/shared/ui/Typography';

import { useSubscriptionByChannelId } from '../model/useSubscriptionByChannelId';

type ТChannelSubscriptionProps = {
  channelId: string;
  /** When set, the connected phone number is shown instead of the tariff. */
  phoneOverride?: string;
};

export const ChannelSubscription = ({
  channelId,
  phoneOverride,
}: ТChannelSubscriptionProps) => {
  const { t } = useTranslation();
  const { data: subscriptionResponse, isLoading, isError } =
    useSubscriptionByChannelId(channelId, { customError: true });

  if (phoneOverride) {
    return (
      <CustomTypography variant="body2" sx={{ fontWeight: 500 }}>
        {phoneOverride}
      </CustomTypography>
    );
  }

  if (isLoading)
    return <Skeleton variant="text" width={80} sx={{ fontSize: '14px' }} />;

  const isExpired =
    subscriptionResponse?.end_date
      ? new Date(subscriptionResponse.end_date) < new Date()
      : false;

  if (isError || !subscriptionResponse || isExpired) {
    return (
      <CustomTypography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
        {t('billing:subscriptionEnded')}
      </CustomTypography>
    );
  }

  const hasChatLimit =
    subscriptionResponse.chat_limit !== undefined &&
    subscriptionResponse.chat_limit !== null;
  const chatQuotaText = hasChatLimit
    ? ` · ${t('billing:chatQuotaUsage', {
        used: subscriptionResponse.used_chats ?? 0,
        limit: subscriptionResponse.chat_limit,
      })}`
    : '';

  return (
    <CustomTypography variant="body2" sx={{ fontWeight: 500 }}>
      {subscriptionResponse.tariff}
      {chatQuotaText}
    </CustomTypography>
  );
};
